import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les sphères sont les seules lumières.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'kinetic',
  family: 'geometric',
  danceability: 0.72,
  requiresTier: 0,
  energyLevel: 0.58,
  bpmMin: 100,
  bpmMax: 135,
  movement: 0.55,
  tonality: 0.65,
  enabled: true
};

// KINETIC : une installation de « kinetic lights » : des centaines de sphères
// lumineuses suspendues à des câbles, chacune sur un treuil, qui montent et
// descendent ensemble et dessinent des surfaces dans l'espace. Deep house,
// techno mélodique, progressive : preset party de vdjLook à intensité modérée.
//
// L'INSTALLATION : une grille de 16 x 16 sphères (12 x 12 au tier 1, 10 x 10 au
// tier 0) dans une salle noire, caméra à 3/4 en légère contre-plongée, qui
// tourne de 10° à chaque mesure. Tout est instancié : les sphères (verre
// dépoli : cœur blanc au centre, couleur de palette, liseré clair), les CÂBLES
// (un quad par sphère, aligné en espace écran, profil R1 par distance en
// pixels : 1,5 px, net sans crénelage), les HALOS (quads additifs qui
// s'éteignent avant leur bord, R12). Au tier 1 et 2, les sphères et les halos
// sont dessinés une seconde fois en miroir sous le sol noir brillant,
// atténués et fondus avec la hauteur. Cible privée MSAA : des solides éclairés
// crénèlent sans elle.
//
// LES TREUILS : les hauteurs cibles viennent de la chorégraphie (vague, ondes,
// selle, dôme, hélice, spectre), amplitude par bands.low ; chaque treuil
// rejoint sa cible à vitesse BORNÉE (1,3 unité/s, 0,5 pendant la montée, 4 au
// drop) : aucune sphère ne saute, et les transitions entre chorégraphies sont
// les treuils qui rattrapent la nouvelle surface. Les hauteurs vivent dans une
// texture de données 16 x 16 (hauteur, teinte, flash, scintillement) mise à
// jour par frame — 4 Ko, pas un attribut.
//
// LA MUSIQUE : temps → une rangée flashe en blanc (la rangée avance) ; bands.high
// → scintillement des sphères les plus hautes ; mesure → la caméra tourne de
// 10° ; phrase → chorégraphie suivante et palette ; montée → tout descend
// lentement et s'aligne à plat ; drop → tout remonte d'un coup en dôme, blanc
// puis palette (flash plein écran seulement si l'intensité dépasse 0,7). Scène
// (lib/scene-clock.js, 40 s) : une chorégraphie par scène, lisible dès la
// première seconde (les treuils partent sur la surface).

const FORMS = ['wave', 'rings', 'saddle', 'dome', 'helix', 'spectrum'];
const TIERS = { 0: { n: 10, reflection: false, msaa: 0 }, 1: { n: 12, reflection: true, msaa: 4 }, 2: { n: 16, reflection: true, msaa: 4 } };
const SPACING = 1.0;
const BASE_H = 2.8, AMP_H = 1.8, CEIL_H = 7.5;
const RADIUS = 0.16;
const FOV_DEG = 48;
const SCENE_S = 40;
const NO_BPM = 120;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h >>> 0; }
const smooth01 = (x) => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t); };

// --- GLSL ------------------------------------------------------------------------------
const DATA_CHUNK = `
  uniform sampler2D uData;      // per sphere: height, hue 0..1, flash, twinkle
  uniform float uN, uSpacing, uMirror, uFlat;
  attribute vec2 aIJ;
  vec4 sphereData() { return texture2D(uData, (aIJ + 0.5) / uN); }
  vec3 sphereCentre(vec4 d) {
    float h = d.x;
    vec3 c = vec3((aIJ.x - 0.5 * (uN - 1.0)) * uSpacing, h, (aIJ.y - 0.5 * (uN - 1.0)) * uSpacing);
    if (uMirror > 0.5) c.y = -c.y;
    return c;
  }
`;
const sphereVS = `
  precision highp float;
  ${DATA_CHUNK}
  uniform float uRadius;
  varying vec3 vN, vV;
  varying vec4 vD;
  varying float vMirrorFade;
  void main() {
    vec4 d = sphereData();
    vec3 c = sphereCentre(d);
    vec3 w = c + position * uRadius;
    vN = uMirror > 0.5 ? vec3(normal.x, -normal.y, normal.z) : normal;
    vV = normalize(cameraPosition - w);
    vD = d;
    vMirrorFade = uMirror > 0.5 ? 0.22 * exp(-max(d.x - 1.0, 0.0) / 2.2) : 1.0;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }
`;
const sphereFS = `
  precision highp float;
  uniform vec3 uC0, uC1, uC2;
  uniform float uWhite, uGain;
  varying vec3 vN, vV;
  varying vec4 vD;
  varying float vMirrorFade;
  vec3 palette(float t) { return t < 0.5 ? mix(uC0, uC1, t * 2.0) : mix(uC1, uC2, t * 2.0 - 1.0); }
  void main() {
    vec3 n = normalize(vN), v = normalize(vV);
    float ndv = max(dot(n, v), 0.0);
    vec3 col = palette(vD.y);
    col = mix(col, vec3(1.0, 0.98, 0.95), uWhite + vD.z);
    // frosted glass: a lit body, a white core at the centre, a thin bright rim
    float core = pow(ndv, 3.0) * 0.55;
    float rim = pow(1.0 - ndv, 4.0) * 0.35;
    vec3 c = col * (0.45 + 0.35 * ndv) + vec3(1.0, 0.98, 0.95) * core * (0.6 + 0.4 * vD.z) + col * rim;
    c *= 1.0 + 0.9 * vD.w;                                   // the twinkle of the highest spheres
    c *= 1.0 + 1.4 * vD.z;                                   // the flashing row
    gl_FragColor = vec4(c * vMirrorFade * uGain, 1.0);
  }
`;
// the cable: a quad per sphere, from the sphere's top to the ceiling, aligned on screen
const cableVS = `
  precision highp float;
  ${DATA_CHUNK}
  uniform float uRadius, uCeil, uPxW, uDebugV;       // half width in pixels
  uniform vec2 uRes;
  varying float vSide;
  void main() {
    vec4 d = sphereData();
    vec3 c = sphereCentre(d);
    vec3 A = c + vec3(0.0, uRadius * 0.9, 0.0), B = vec3(c.x, uCeil, c.z);
    vec4 ca = projectionMatrix * viewMatrix * vec4(A, 1.0), cb = projectionMatrix * viewMatrix * vec4(B, 1.0);
    float s = position.y + 0.5;                             // 0 at the sphere, 1 at the ceiling
    // in NDC: a straight segment stays straight on screen, so mixing the two projected ends
    // walks the right line; the half width is laid across it in pixels
    vec3 na = ca.xyz / ca.w, nb = cb.xyz / cb.w;
    vec3 nd = mix(na, nb, s);
    vec2 dir = normalize((nb.xy - na.xy) * uRes);
    vec2 perp = vec2(-dir.y, dir.x) / uRes * 2.0;
    vSide = position.x * 2.0;                               // -1..1 across
    nd.xy += perp * (uPxW + 1.0) * vSide;
    gl_Position = vec4(nd, 1.0);
    if (uDebugV > 0.5) gl_Position = projectionMatrix * viewMatrix * vec4(mix(A, B, s) + vec3(position.x * 0.3, 0.0, 0.0), 1.0);
  }
`;
const cableFS = `
  precision highp float;
  uniform float uPxW, uGain, uDebug;
  varying float vSide;
  void main() {
    if (uDebug > 0.5) { gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0); return; }
    float dpx = abs(vSide) * (uPxW + 1.0);
    float a = 1.0 - smoothstep(uPxW - 0.6, uPxW + 0.6, dpx);
    if (a < 0.01) discard;
    // a steel cable in a dark hall: grey, a faint lit centre
    vec3 c = vec3(0.30, 0.31, 0.33) * (0.6 + 0.4 * (1.0 - smoothstep(0.0, uPxW, dpx)));
    gl_FragColor = vec4(c * a * uGain, a);
  }
`;
// the halo: an additive billboard that reaches zero well before its edge (R12)
const haloVS = `
  precision highp float;
  ${DATA_CHUNK}
  uniform float uRadius, uHaloK;
  varying vec2 vQ;
  varying vec4 vD;
  varying float vMirrorFade;
  void main() {
    vec4 d = sphereData();
    vec3 c = sphereCentre(d);
    vec4 vc = viewMatrix * vec4(c, 1.0);
    vc.xy += position.xy * uRadius * uHaloK;
    vQ = position.xy * 2.0;
    vD = d;
    vMirrorFade = uMirror > 0.5 ? 0.25 * exp(-max(d.x - 1.0, 0.0) / 2.2) : 1.0;
    gl_Position = projectionMatrix * vc;
  }
`;
const haloFS = `
  precision highp float;
  uniform vec3 uC0, uC1, uC2;
  uniform float uWhite, uGain, uHaze;
  varying vec2 vQ;
  varying vec4 vD;
  varying float vMirrorFade;
  vec3 palette(float t) { return t < 0.5 ? mix(uC0, uC1, t * 2.0) : mix(uC1, uC2, t * 2.0 - 1.0); }
  void main() {
    float r = length(vQ);
    float g = exp(-r * 7.0) * (1.0 - smoothstep(0.55, 0.95, r));
    vec3 col = mix(palette(vD.y), vec3(1.0, 0.98, 0.95), uWhite + vD.z * 0.7);
    gl_FragColor = vec4(col * g * uHaze * (1.0 + 2.0 * vD.z + 0.8 * vD.w) * vMirrorFade * uGain, 1.0);
  }
`;
const floorVS = `
  precision highp float;
  varying vec3 vW;
  void main() { vW = position; gl_Position = projectionMatrix * viewMatrix * vec4(position, 1.0); }
`;
const floorFS = `
  precision highp float;
  varying vec3 vW;
  void main() {
    // a black gloss floor: a faint sheen toward the camera, nothing else (the reflections are meshes)
    vec3 v = normalize(cameraPosition - vW);
    float sheen = pow(1.0 - max(v.y, 0.0), 8.0) * 0.002;
    gl_FragColor = vec4(vec3(0.0) + sheen, 1.0);
  }
`;
const displayVS = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const displayFS = `
  precision highp float;
  uniform sampler2D uTex;
  uniform float uAspect;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec3 col = texture2D(uTex, vUv).rgb;
    vec2 qq = abs((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, dz));
    gl_FragColor = vec4(col, 1.0);
  }
`;

function instanced(base, n) {
  const g = new THREE.InstancedBufferGeometry();
  g.index = base.index; g.setAttribute('position', base.attributes.position);
  if (base.attributes.normal) g.setAttribute('normal', base.attributes.normal);
  const ij = new Float32Array(n * n * 2);
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) { ij[(j * n + i) * 2] = i; ij[(j * n + i) * 2 + 1] = j; }
  g.setAttribute('aIJ', new THREE.InstancedBufferAttribute(ij, 2));
  g.instanceCount = n * n;
  return g;
}

export class KineticVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIERS[o.qualityTier] ? o.qualityTier : 1;
    this._T = TIERS[this._tier];
    const gn = parseInt(urlParam('kingrid'), 10);
    this._n = Number.isFinite(gn) ? Math.max(4, Math.min(24, gn)) : this._T.n;
    const f = urlParam('kinform');
    this._forceForm = f && FORMS.includes(f) ? f : null;
    const cp = parseFloat(urlParam('kincable'));
    this._cablePx = Number.isFinite(cp) ? cp : null;
    this._adapt = new Adapt();
    this._post = new VdjPost({ bloomStrength: 0.4, bloomThreshold: 0.78, grain: 0.01, vignette: 0.3, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    if (urlParam('kinprobe') === '1' && typeof window !== 'undefined') window.__kinetic = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const n = this._n, N = n * n;
    this._cam = new THREE.PerspectiveCamera(FOV_DEG, 1.78, 0.1, 200);
    this._private = new THREE.Scene();
    this._tanH = Math.tan(FOV_DEG / 2 * Math.PI / 180);

    // the data texture: height, hue, flash, twinkle per sphere
    this._data = new Float32Array(N * 4);
    this._tex = new THREE.DataTexture(this._data, n, n, THREE.RGBAFormat, THREE.FloatType);
    this._tex.minFilter = this._tex.magFilter = THREE.NearestFilter;
    this._tex.needsUpdate = true;
    this._h = new Float32Array(N); this._flash = new Float32Array(N); this._tw = new Float32Array(N); this._rnd1 = new Float32Array(N);
    let seed = 99; const rnd = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
    for (let k = 0; k < N; k++) this._rnd1[k] = rnd();

    const common = { uData: { value: this._tex }, uN: { value: n }, uSpacing: { value: SPACING }, uFlat: { value: 0 } };
    this._colU = { uC0: { value: new THREE.Color(0.1, 0.6, 1) }, uC1: { value: new THREE.Color(0.9, 0.2, 0.7) }, uC2: { value: new THREE.Color(1, 0.7, 0.2) }, uWhite: { value: 0 }, uGain: { value: 1 }, uHaze: { value: 0.55 } };
    const mk = (vs, fs, extra, opts) => new THREE.ShaderMaterial(Object.assign({ uniforms: Object.assign({}, common, this._colU, extra), vertexShader: vs, fragmentShader: fs }, opts));
    const sphereGeo = new THREE.SphereGeometry(1, 18, 12);
    const quadGeo = new THREE.PlaneGeometry(1, 1);
    this._geos = [sphereGeo, quadGeo];
    this._meshes = [];
    const add = (geo, mat, order) => { const m = new THREE.Mesh(geo, mat); m.frustumCulled = false; m.renderOrder = order; this._private.add(m); this._meshes.push(m); return m; };
    // the floor
    this._floor = add(new THREE.PlaneGeometry(400, 400).rotateX(-Math.PI / 2), new THREE.ShaderMaterial({ uniforms: {}, vertexShader: floorVS, fragmentShader: floorFS }), 0);
    // the mirrored copies first (under the floor, drawn before it would be hidden: the floor is
    // at y = 0 and the copies below it; the floor mesh has no depth write for them to show)
    this._floor.material.depthWrite = false;
    if (this._T.reflection) {
      add(instanced(sphereGeo, n), mk(sphereVS, sphereFS, { uRadius: { value: RADIUS }, uMirror: { value: 1 } }, {}), 1);
      add(instanced(quadGeo, n), mk(haloVS, haloFS, { uRadius: { value: RADIUS }, uMirror: { value: 1 }, uHaloK: { value: 4.5 } }, { transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }), 2);
    }
    add(instanced(quadGeo, n), mk(cableVS, cableFS, { uRadius: { value: RADIUS }, uMirror: { value: 0 }, uCeil: { value: CEIL_H }, uPxW: { value: 0.75 }, uRes: { value: new THREE.Vector2(1600, 900) }, uDebug: { value: urlParam('kindebug') === '1' ? 1 : 0 }, uDebugV: { value: urlParam('kindebug') === '2' ? 1 : 0 } }, { transparent: urlParam('kinopaque') !== '1', depthWrite: false, side: THREE.DoubleSide }), 3);   // the screen-aligned quad winds either way
    add(instanced(sphereGeo, n), mk(sphereVS, sphereFS, { uRadius: { value: RADIUS }, uMirror: { value: 0 } }, {}), 4);
    add(instanced(quadGeo, n), mk(haloVS, haloFS, { uRadius: { value: RADIUS }, uMirror: { value: 0 }, uHaloK: { value: 4.5 } }, { transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }), 5);
    this._cableMat = this._meshes[this._T.reflection ? 3 : 1].material;

    this._target = null;
    this._dispU = { uTex: { value: null }, uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: displayVS, fragmentShader: displayFS, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._seed = 5; this._trackSeen = undefined;
    this._time = 0; this._freePhase = 0; this._beats = 0;
    this._formIdx = 0; this._form = this._forceForm || FORMS[0]; this._formT = 0;
    this._colourMode = 0; this._palShift = 0;
    this._row = 0; this._camAz = 0.6; this._camAzTarget = 0.6;
    this._build = 0; this._dropT = -1; this._white = 0; this._intensity = 0.3;
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._log = [];
    // the winches start on the surface: readable from the first second
    this._amp = 0.6;
    for (let k = 0; k < N; k++) this._h[k] = this._targetH(k, 0);
    this._writeData();
  }

  _rnd() { this._seed = (Math.imul(this._seed, 1664525) + 1013904223) >>> 0; return this._seed / 4294967296; }
  _note(e) { e.t = +this._time.toFixed(2); this._log.push(e); if (this._log.length > 30) this._log.shift(); }

  // the choreography's target height for sphere k at scene time t
  _targetH(k, t) {
    const n = this._n, i = k % n, j = Math.floor(k / n);
    const x = (i - (n - 1) / 2) / ((n - 1) / 2), z = (j - (n - 1) / 2) / ((n - 1) / 2);   // -1..1
    const r = Math.hypot(x, z), phi = Math.atan2(z, x);
    const A = AMP_H * this._amp;
    let f = 0;
    switch (this._form) {
      case 'wave': { const d = t * 0.15; f = Math.sin((x * Math.cos(d) + z * Math.sin(d)) * 3.2 - t * 1.6); break; }
      case 'rings': f = Math.sin(r * 5.5 - t * 1.8); break;
      case 'saddle': f = r * r * Math.cos(2 * phi - t * 0.7); break;
      case 'dome': f = 2.2 * Math.exp(-r * r * 2.2) * (0.55 + 0.45 * Math.sin(t * 1.3)) - 0.6; break;
      case 'helix': f = Math.sin(2 * phi + r * 4.5 - t * 1.4); break;
      case 'spectrum': {
        const b = this._bands || { low: 0.3, mid: 0.3, high: 0.2 };
        const u = (j + 0.5) / n;                                    // row -> frequency
        const lvl = u < 0.5 ? b.low * (1 - u * 2) + b.mid * u * 2 : b.mid * (2 - u * 2) + b.high * (u * 2 - 1);
        f = (lvl * 2.4 - 1) * (0.8 + 0.2 * Math.sin(x * 6 + t * 2 + j));
        break;
      }
    }
    return BASE_H + A * f;
  }

  _writeData() {
    const N = this._n * this._n, d = this._data, hs = this._h;
    let hmin = 1e9, hmax = -1e9;
    for (let k = 0; k < N; k++) { if (hs[k] < hmin) hmin = hs[k]; if (hs[k] > hmax) hmax = hs[k]; }
    const span = Math.max(hmax - hmin, 0.3);
    for (let k = 0; k < N; k++) {
      const hn = (hs[k] - hmin) / span;
      const i = k % this._n, j = Math.floor(k / this._n);
      const hue = this._colourMode === 0 ? hn : ((i + j) / (2 * this._n) * 1.5 + this._time * 0.08) % 1;
      d[k * 4] = hs[k]; d[k * 4 + 1] = hue; d[k * 4 + 2] = this._flash[k]; d[k * 4 + 3] = this._tw[k];
    }
    this._tex.needsUpdate = true;
  }

  _colours(profile, a) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors || pal.dominantColors)) || [];
    const hsl = [];
    for (let k = 0; k < 3; k++) {
      const c = _tone[k];
      if (src.length) vdjColor(src[(k + this._palShift) % src.length], c); else c.setHSL((0.58 + k * 0.33 + this._palShift * 0.11) % 1, 0.9, 0.55);
      const o = { h: 0, s: 0, l: 0 }; c.getHSL(o); hsl.push(o);
    }
    for (let k = 1; k < 3; k++) for (let j = 0; j < k; j++) { const d = Math.abs(hsl[k].h - hsl[j].h); if (Math.min(d, 1 - d) < 0.25) { hsl[k].h = (hsl[0].h + k / 3) % 1; _tone[k].setHSL(hsl[k].h, 0.9, 0.55); } }
    this._colU.uC0.value.copy(_tone[0]); this._colU.uC1.value.copy(_tone[1]); this._colU.uC2.value.copy(_tone[2]);
  }

  _nextForm() {
    if (this._forceForm) return;
    const k = (FORMS.indexOf(this._form) + 1 + Math.floor(this._rnd() * (FORMS.length - 1))) % FORMS.length;
    this._form = FORMS[k];
    this._palShift = (this._palShift + 1) % 3;
    this._colourMode = this._rnd() < 0.5 ? 0 : 1;
    this._note({ what: 'form', form: this._form });
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING';
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._seed = hashStr(String(profile.title || '') + '|' + String(profile.artist || '')) || 5;
    }
    this._colours(profile, a);
    if (!playing) return;                                        // frozen, never black
    this._time += dt;
    this._clock.update(dt);
    this._intensity += (a.intensity - this._intensity) * Math.min(1, dt / 2);
    const I = this._intensity, t = this._time;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const beatS = 60 / bpm;
    const pulsed = a.pulse > 0.25;
    let beat = a.beat, phase;
    if (pulsed) phase = a.beatPhase;
    else { this._freePhase += dt / beatS; if (this._freePhase >= 1) { this._freePhase -= 1; beat = true; } phase = this._freePhase; }
    if (beat) this._beats++;
    const bar = a.bar || (!pulsed && beat && this._beats % 4 === 0);
    const phrase = a.phrase || (!pulsed && beat && this._beats % 16 === 0);
    const b = a.bands || { low: 0.3 + 0.3 * I, mid: 0.3, high: 0.2 + 0.3 * I };
    this._bands = b;

    // the climb and the drop
    const director = profile.director;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * Math.min(1, dt / 0.8);
    if (a.drop && this._dropT < 0) {
      this._dropT = 0; this._white = 1;
      if (I > 0.7 && director && this._comfort.requestFlash(t)) director.flash = VDJ_DROP_FLASH;
      this._note({ what: 'drop' });
    }
    if (this._dropT >= 0) { this._dropT += dt; if (this._dropT > 4 * beatS) this._dropT = -1; }
    this._white = Math.max(0, this._white - dt / (2 * beatS));

    // the scene and the phrase: the next choreography; the bar: the camera turns 10 degrees
    if ((phrase || this._clock.wrapped) && this._dropT < 0) this._nextForm();
    if (bar) this._camAzTarget += 10 * Math.PI / 180;
    this._camAz += (this._camAzTarget - this._camAz) * Math.min(1, dt / 1.2);

    // the targets: the choreography, flattened in the climb, a dome at the drop
    this._amp += ((0.35 + 0.65 * b.low) - this._amp) * Math.min(1, dt / 0.4);
    const N = this._n * this._n;
    const inDrop = this._dropT >= 0 && this._dropT < 2 * beatS;
    let vmax = 1.3;
    if (this._build > 0.3) vmax = 0.5;
    if (inDrop) vmax = 4.0;
    const formSaved = this._form, ampSaved = this._amp;
    if (inDrop) { this._form = 'dome'; this._amp = 1.3; }
    const step = vmax * dt;
    for (let k = 0; k < N; k++) {
      let tg = this._targetH(k, this._clock.elapsed);
      if (this._build > 0.3 && !inDrop) tg = tg * (1 - this._build) + (BASE_H - 0.9) * this._build;   // down and flat
      const d = tg - this._h[k];
      this._h[k] += Math.max(-step, Math.min(step, d));
    }
    this._form = formSaved; this._amp = ampSaved;

    // the beat: a row flashes, advancing; the highs: the highest spheres twinkle
    if (beat) { this._row = (this._row + 1) % this._n; for (let i = 0; i < this._n; i++) this._flash[this._row * this._n + i] = 1; }
    const fdec = Math.pow(0.5, dt / (0.18 * beatS));
    let hmax = -1e9; for (let k = 0; k < N; k++) hmax = Math.max(hmax, this._h[k]);
    for (let k = 0; k < N; k++) {
      this._flash[k] *= fdec;
      const high = this._h[k] > hmax - 0.5;
      const target = high ? b.high * (0.5 + 0.5 * Math.sin(t * (6 + 9 * this._rnd1[k]) + this._rnd1[k] * 40)) : 0;
      this._tw[k] += (target - this._tw[k]) * Math.min(1, dt / 0.08);
    }
    this._writeData();
    this._colU.uWhite.value = this._white;
    this._colU.uGain.value = Math.min(1, a.lumaCap / 0.42);
    this._post.update(1 + (vdjBeatChroma(phase, beatS) - 1) * Math.max(0, (I - 0.6) / 0.4) * 0.6);
  }

  // the camera: 3/4 view, a little below the grid's base, orbiting; the whole grid stays framed
  _placeCamera(aspect) {
    const n = this._n, half = (n - 1) / 2 * SPACING;
    const cam = this._cam;
    const fovV = Math.max(FOV_DEG, 2 * Math.atan(Math.tan(28 * Math.PI / 180) / aspect) * 180 / Math.PI);
    if (Math.abs(cam.fov - fovV) > 1e-3) { cam.fov = fovV; cam.updateProjectionMatrix(); }
    const tanV = Math.tan(fovV / 2 * Math.PI / 180), tanHh = tanV * aspect;
    // the grid's projected half width (its diagonal at worst) fills 90 % of the frame's width;
    // its height on screen is small (the view is nearly level), so only the width constrains
    const az = this._camAz;
    const centre = new THREE.Vector3(0, BASE_H, 0);
    // slightly below the spheres' base: a visitor's view, looking a little up. The distance is
    // fitted so the eight corners of the installation's box project inside 90 % of the frame
    // (the near corners loom: the grid's centre is not enough)
    const camY = BASE_H - AMP_H - 0.4;
    let dist = half * 2.2;
    const corners = [];
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) for (const sy of [-1, 1]) corners.push(new THREE.Vector3(sx * (half + 0.6), BASE_H + sy * (AMP_H + 0.6), sz * (half + 0.6)));
    for (let it = 0; it < 10; it++) {
      const dh = Math.sqrt(Math.max(dist * dist - (centre.y - camY) * (centre.y - camY), 1));
      cam.position.set(dh * Math.cos(az), camY, dh * Math.sin(az));
      cam.up.set(0, 1, 0); cam.lookAt(centre); cam.updateMatrixWorld(); cam.updateProjectionMatrix();
      let worst = 0;
      for (const c of corners) { const p = _tmp.copy(c).project(cam); worst = Math.max(worst, Math.abs(p.x), Math.abs(p.y)); }
      if (Math.abs(worst - 0.95) < 0.01) break;
      dist *= Math.pow(worst / 0.95, 0.7);
    }
    const dh = Math.sqrt(Math.max(dist * dist - (centre.y - camY) * (centre.y - camY), 1));
    cam.position.set(dh * Math.cos(az), camY, dh * Math.sin(az));
    cam.up.set(0, 1, 0);
    cam.lookAt(centre);
    cam.updateMatrixWorld();
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const bsz = renderer.getDrawingBufferSize(_size); w = bsz.x; h = bsz.y; }
    const cam = this._cam;
    if (Math.abs(cam.aspect - w / h) > 1e-4) { cam.aspect = w / h; cam.updateProjectionMatrix(); }
    this._placeCamera(w / h);
    this._cableMat.uniforms.uRes.value.set(w, h);
    this._cableMat.uniforms.uPxW.value = (this._cablePx || 0.75) * Math.max(1, h / 1080);
    if (!this._target || this._target.width !== w || this._target.height !== h) {
      if (this._target) this._target.dispose();
      const msaa = renderer.capabilities.isWebGL2 ? this._T.msaa : 0;
      this._target = new THREE.WebGLRenderTarget(w, h, { type: THREE.UnsignedByteType, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, samples: msaa });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._target);
    renderer.clear(true, true, false);
    renderer.render(this._private, cam);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uTex.value = this._target.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    if (this._display) { this._display.geometry.dispose(); this._display.material.dispose(); }
    for (const m of this._meshes || []) { m.geometry.dispose(); m.material.dispose(); }
    for (const g of this._geos || []) g.dispose();
    if (this._tex) this._tex.dispose();
    if (this._target) this._target.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__kinetic === this) window.__kinetic = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
const _tone = [new THREE.Color(), new THREE.Color(), new THREE.Color()];
const _tmp = new THREE.Vector3();
